import React, { useState } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Space,
  message,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/vi_VN';
import { invoiceService } from '../../services';
import { Invoice } from '../../types';
import { formatDate, formatCurrency } from '../../utils/format';
import InvoiceDetailModal from '../../components/InvoiceDetailModal';
import './InvoiceManagementPage.css';

const InvoiceManagementPage: React.FC = () => {
  const [form] = Form.useForm();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [filters, setFilters] = useState({
    invoiceNumber: '',
    patientName: '',
    service: '',
    startDate: '',
    endDate: '',
  });

  const { data: invoiceResponse, isLoading } = useQuery({
    queryKey: ['invoices', filters, currentPage, pageSize],
    queryFn: () => invoiceService.getList({ ...filters, page: currentPage, pageSize }),
  });

  const onSearch = (values: any) => {
    setFilters({
      invoiceNumber: values.invoiceNumber || '',
      patientName: values.patientName || '',
      service: values.service || '',
      startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : '',
      endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : '',
    });
  };

  const handleViewDetail = (id: number) => {
    setSelectedInvoiceId(id);
    setDetailModalVisible(true);
  };

  const handleDownload = (invoiceNumber: string) => {
    message.success(`Đang tải hóa đơn ${invoiceNumber}`);
  };

  const handleExportReport = () => {
    message.success('Đang xuất báo cáo...');
    // TODO: Implement export report functionality
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '';
    const date = dayjs(dateString);
    return date.format('DD/MM/YYYY HH:mm');
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 60,
    },
    {
      title: 'Số hóa đơn',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (text: string, record: Invoice) => (
        <Button
          type="link"
          onClick={() => handleViewDetail(record.id)}
          style={{ padding: 0, color: '#385da4' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Tên khách hàng',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: 'CCCD/MST',
      dataIndex: 'customer_tax_code',
      key: 'customer_tax_code',
    },
    {
      title: 'Dịch vụ',
      dataIndex: 'service_name',
      key: 'service_name',
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right' as const,
      render: (amount: string) => formatCurrency(parseFloat(amount)),
    },
    {
      title: 'Ngày tạo đơn',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDateTime(date),
    },
    {
      title: 'Ngày gửi đơn',
      dataIndex: 'sent_at',
      key: 'sent_at',
      render: (date: string | null) => (date ? formatDateTime(date) : '-'),
    },
    {
      title: 'Xem hóa đơn',
      key: 'view',
      width: 150,
      render: (_: any, record: Invoice) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.id)}
          style={{ color: '#385da4' }}
        >
          Xem chi tiết
        </Button>
      ),
    },
    {
      title: 'Tải về',
      key: 'download',
      width: 100,
      render: (_: any, record: Invoice) => (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record.invoice_number)}
          style={{ color: '#385da4' }}
        />
      ),
    },
  ];

  const invoices = invoiceResponse?.data || [];
  const tableData = invoices.map((invoice, index) => ({
    ...invoice,
    index: (currentPage - 1) * pageSize + index + 1,
    key: invoice.id,
  }));

  return (
    <div className="invoice-management-page">
      <h1 className="page-title">
        Quản lý hóa đơn ngày {formatDate(dayjs().format('YYYY-MM-DD'))}
      </h1>

      <Form
        form={form}
        layout="inline"
        onFinish={onSearch}
        className="search-form"
      >
        <Form.Item name="invoiceNumber">
          <Input placeholder="Số hóa đơn" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="patientName">
          <Input placeholder="Tên bệnh nhân" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="service">
          <Input placeholder="Dịch vụ" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="startDate">
          <DatePicker
            placeholder="Từ ngày"
            format="DD/MM/YYYY"
            locale={locale}
          />
        </Form.Item>
        <Form.Item name="endDate">
          <DatePicker
            placeholder="Đến ngày"
            format="DD/MM/YYYY"
            locale={locale}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              Tìm kiếm
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportReport}
            >
              Xuất báo cáo
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        columns={columns}
        dataSource={tableData}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: invoiceResponse?.meta?.count || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Tổng ${total} hóa đơn`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      <InvoiceDetailModal
        visible={detailModalVisible}
        invoiceId={selectedInvoiceId}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedInvoiceId(null);
        }}
      />
    </div>
  );
};

export default InvoiceManagementPage;

